import {
	createReactRouterV6Options,
	faro,
	getWebInstrumentations,
	initializeFaro,
	ReactIntegration,
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import * as Sentry from '@sentry/react';
import axios, { isAxiosError } from 'axios';
import { ErrorInfo } from 'react';
import { createRoutesFromChildren, matchRoutes, Routes, useLocation, useNavigationType } from 'react-router-dom';
import { autorun } from 'mobx';
import { store } from 'Models/Store';

/*
 * Frontend observability, driven entirely by window.observability (emitted by
 * /api/clientside-config.js — see ClientsideConfig.cshtml.cs):
 *   - Grafana Faro: unhandled errors, web vitals, and browser→backend traces
 *     (traceparent on same-origin /api calls). Session tracking is deliberately
 *     disabled — errors/vitals/traces only, so no consent gating is required.
 *   - Sentry SDK pointed at self-hosted GlitchTip: error grouping + issue workflow.
 * Both no-op when their config values are empty, so local dev and tenants without
 * the rollout values are unaffected.
 */

let faroInitialized = false;
let sentryInitialized = false;

/** Whether initializeFaro ran (init happens before the React root renders). */
export function isFaroInitialized(): boolean {
	return faroInitialized;
}

export function initObservability() {
	const config = window.observability;
	if (!config) {
		return;
	}

	if (config.faroCollectorUrl) {
		initFaro(config.faroCollectorUrl, config.faroApiKey, config.environment);
	}
	if (config.sentryDsn) {
		initSentry(config.sentryDsn, config.environment);
	}

	if (faroInitialized) {
		installApiErrorBreadcrumbs();
	}

	if (faroInitialized || sentryInitialized) {
		// Keep the reported user in sync with the MobX store (covers login, logout and
		// the initial window.loginData hydration) without touching the auth flows.
		autorun(() => {
			const { userId } = store;
			if (faroInitialized) {
				if (userId) {
					faro.api.setUser({ id: userId });
				} else {
					faro.api.resetUser();
				}
			}
			if (sentryInitialized) {
				Sentry.setUser(userId ? { id: userId } : null);
			}
		});
	}
}

function initFaro(collectorUrl: string, apiKey: string, environment: string) {
	initializeFaro({
		url: collectorUrl,
		// Public abuse-friction checked by Alloy's faro.receiver (x-api-key), not a secret
		apiKey: apiKey || undefined,
		app: {
			name: 'ferryhawk-frontend',
			version: window.buildVersion?.buildRef,
			environment: environment || undefined,
		},
		sessionTracking: {
			enabled: false,
		},
		instrumentations: [
			...getWebInstrumentations(),
			new TracingInstrumentation(),
			new ReactIntegration({
				router: createReactRouterV6Options({
					createRoutesFromChildren,
					matchRoutes,
					Routes,
					useLocation,
					useNavigationType,
				}),
			}),
		],
	});
	faroInitialized = true;
}

function initSentry(dsn: string, environment: string) {
	Sentry.init({
		dsn,
		release: window.buildVersion?.buildRef,
		environment: environment || undefined,
		// Errors raised by scripts the host browser injects into our page. They carry no frame of
		// ours, can't be fixed from here, and drown the real issues.
		denyUrls: [
			// Facebook's in-app browser (FB_IAB) injects its own instrumentation under this scheme —
			// browser_declutter, navigation_performance_logger_android and friends.
			/^iabjs:\/\//i,
			// Browser extensions. Safari masks its extension scripts behind webkit-masked-url
			// rather than a scheme naming the extension.
			/^(chrome|moz|safari-web)-extension:\/\//i,
			/^webkit-masked-url:\/\//i,
		],
		ignoreErrors: [
			// Outlook's link scanner runs this against pages opened from an email. Not our code, and
			// it fires on any tenant whose customers click a booking link out of Outlook.
			/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
		],
		beforeSend: event => {
			// Error grouping needs stacks and URLs, not payloads — keep PII out of GlitchTip
			if (event.request) {
				delete event.request.cookies;
				delete event.request.data;
			}
			return event;
		},
	});
	Sentry.setTag('tenant', window.location.hostname);
	sentryInitialized = true;
}

/**
 * Pushes a Faro breadcrumb event, no-oping when Faro is not initialized (local dev and
 * tenants without the rollout config). Attribute values must be strings and must not
 * contain PII — no query strings, no payloads, no free-typed user input.
 */
export function pushObservabilityEvent(name: string, attributes?: Record<string, string>) {
	if (!faroInitialized) {
		return;
	}
	faro.api.pushEvent(name, attributes);
}

/**
 * Breadcrumb-only visibility of failed API calls (network errors and >= 400 responses)
 * on the shared global axios instance. Deliberately not reported to Sentry — these are
 * events for correlating with errors and traces, not exceptions in their own right.
 * Query strings and fragments are stripped from the URL so no PII lands in the event.
 * The error/response is passed through unchanged.
 */
function installApiErrorBreadcrumbs() {
	const pushApiError = (method?: string, url?: string, status?: number) => {
		pushObservabilityEvent('api_error', {
			method: method?.toUpperCase() ?? 'UNKNOWN',
			path: (url ?? '').split(/[?#]/)[0],
			status: status !== undefined ? String(status) : 'network-error',
		});
	};

	axios.interceptors.response.use(
		response => {
			// Some callers opt out of axios' default validateStatus, so error statuses can
			// arrive through the fulfilled path (see SessionInterceptor's 401 handling).
			if (response.status >= 400) {
				pushApiError(response.config?.method, response.config?.url, response.status);
			}
			return response;
		},
		(error: unknown) => {
			if (isAxiosError(error)) {
				pushApiError(error.config?.method, error.config?.url, error.response?.status);
			}
			return Promise.reject(error);
		},
	);
}

/**
 * Called by the top-level ErrorBoundary: render-path errors are swallowed by React
 * and never reach window.onerror, so the global handlers of both SDKs miss them.
 */
export function reportBoundaryError(error: Error, errorInfo: ErrorInfo) {
	if (faroInitialized) {
		faro.api.pushError(error, {
			context: { componentStack: errorInfo.componentStack ?? '' },
		});
	}
	if (sentryInitialized) {
		Sentry.captureException(error, {
			contexts: { react: { componentStack: errorInfo.componentStack } },
		});
	}
}

/**
 * Report a handled (caught) error we recover from but still want visibility on — e.g. a payment
 * tokenisation failure that's surfaced to the user rather than crashing the app. Sends a Faro event
 * (for trace correlation) and a Sentry exception tagged by `event` with the structured `attributes`
 * as searchable context. No-ops when neither SDK is configured (local dev / un-rolled-out tenants).
 */
export function reportHandledError(
	error: unknown,
	context: { event: string; attributes?: Record<string, string> },
) {
	if (faroInitialized) {
		faro.api.pushEvent(context.event, context.attributes);
	}
	if (sentryInitialized) {
		Sentry.captureException(error, {
			tags: { handledEvent: context.event },
			extra: context.attributes,
		});
	}
}
